import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  Sparkles, Copy, ExternalLink, Loader2, Wallet, Users, TrendingUp,
  ArrowRight, FileText,
} from "lucide-react";
import api, { formatApiError } from "../api";
import { Button, Card, EmptyState, Badge, Spinner, Input, Label } from "../components/ui/Primitives";
import { formatEUR as fmtEur } from "../utils/format";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

export default function AffiliatePage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [payoutEmail, setPayoutEmail] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const me = await api.get("/affiliates/me");
      if (!me.data?.affiliate) {
        setData({ affiliate: null });
      } else {
        const r = await api.get("/affiliates/me/dashboard");
        setData(r.data);
        setPayoutEmail(r.data.affiliate?.payout_email || "");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copyLink = () => {
    if (!data?.affiliate?.referral_code) return;
    const url = `${window.location.origin}/?ref=${data.affiliate.referral_code}`;
    const ok = () => toast.success(t("affiliate.link_copied"));
    const fb = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        ok();
      } catch { toast(t("affiliate.copy_manual", "Bitte Strg+C zum Kopieren drücken.")); }
    };
    try {
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(url).then(ok, fb);
      else fb();
    } catch { fb(); }
  };

  const requestPayout = async () => {
    setRequesting(true);
    try {
      await api.post("/affiliates/me/request-payout", { payout_email: payoutEmail || undefined });
      toast.success(t("affiliate.payout_requested"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRequesting(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Spinner size={24} /></div>;
  }

  // Not yet an affiliate
  if (!data?.affiliate) {
    return (
      <div className="space-y-6 fade-in" data-testid="affiliate-page">
        <header>
          <div className="text-[11px] uppercase tracking-wider text-brand mb-1 flex items-center gap-2">
            <Sparkles size={12} /> {t("affiliate.kicker")}
          </div>
          <h1 className="text-2xl font-semibold">{t("affiliate.title")}</h1>
        </header>
        <EmptyState
          icon={Sparkles}
          title={t("affiliate.no_account_title")}
          description={t("affiliate.no_account_sub")}
          action={
            <Button onClick={() => window.open("/partner", "_blank")} data-testid="affiliate-apply-cta">
              <ArrowRight size={14} /> {t("affiliate.apply_now")}
            </Button>
          }
        />
      </div>
    );
  }

  const aff = data.affiliate;
  const balance = data.balance || { pending_eur: 0, paid_eur: 0, lifetime_eur: 0 };
  const stats = data.stats || { referrals: 0, converted: 0 };
  const minPayout = data.min_payout_eur || 50;
  const canPayout = balance.pending_eur >= minPayout && aff.status === "active";
  const refUrl = `${window.location.origin}/?ref=${aff.referral_code}`;

  return (
    <div className="space-y-6 fade-in" data-testid="affiliate-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-brand mb-1 flex items-center gap-2">
            <Sparkles size={12} /> {t("affiliate.kicker")}
          </div>
          <h1 className="text-2xl font-semibold text-ink-primary">{t("affiliate.title")}</h1>
          <p className="text-sm text-ink-secondary mt-1 max-w-2xl">{t("affiliate.subtitle", { pct: data.commission_pct, days: data.window_days })}</p>
        </div>
        <Badge tone={aff.status === "active" ? "ok" : aff.status === "pending" ? "warn" : "err"}>
          {t(`affiliate.status_${aff.status}`, aff.status)}
        </Badge>
      </header>

      {aff.status === "pending" && (
        <Card className="p-4 bg-warn/10 border-warn/30" data-testid="affiliate-pending-banner">
          <p className="text-sm text-ink-primary">{t("affiliate.pending_banner")}</p>
        </Card>
      )}

      {/* Referral link */}
      <Card className="p-5">
        <div className="text-xs uppercase tracking-wider text-ink-muted mb-2">{t("affiliate.your_link")}</div>
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2">
          <code className="flex-1 bg-bg-void rounded-md px-3 py-2 text-sm border border-white/10 truncate" data-testid="affiliate-link">
            {refUrl}
          </code>
          <Button variant="secondary" onClick={copyLink} data-testid="affiliate-copy">
            <Copy size={14} /> {t("affiliate.copy")}
          </Button>
          <Button variant="ghost" onClick={() => window.open(refUrl, "_blank")} data-testid="affiliate-preview">
            <ExternalLink size={14} /> {t("affiliate.preview")}
          </Button>
        </div>
        <p className="text-xs text-ink-muted mt-2">{t("affiliate.link_hint", { days: data.window_days })}</p>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="affiliate-kpis">
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wider text-ink-muted flex items-center gap-1.5"><Users size={11} /> {t("affiliate.kpi_referrals")}</div>
          <div className="num text-2xl font-medium">{stats.referrals}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wider text-ink-muted flex items-center gap-1.5"><TrendingUp size={11} /> {t("affiliate.kpi_converted")}</div>
          <div className="num text-2xl font-medium">{stats.converted}</div>
        </Card>
        <Card className="p-4 border-warn/30">
          <div className="text-[10px] uppercase tracking-wider text-ink-muted">{t("affiliate.kpi_pending")}</div>
          <div className="num text-2xl font-medium text-warn">{fmtEur(balance.pending_eur)}</div>
        </Card>
        <Card className="p-4 border-ok/30">
          <div className="text-[10px] uppercase tracking-wider text-ink-muted">{t("affiliate.kpi_paid")}</div>
          <div className="num text-2xl font-medium text-ok">{fmtEur(balance.paid_eur)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wider text-ink-muted">{t("affiliate.kpi_lifetime")}</div>
          <div className="num text-2xl font-medium">{fmtEur(balance.lifetime_eur)}</div>
        </Card>
      </div>

      {/* Payout */}
      <Card className="p-5 space-y-3" data-testid="affiliate-payout-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-base font-semibold flex items-center gap-2"><Wallet size={16} className="text-brand" /> {t("affiliate.payout_title")}</div>
            <p className="text-xs text-ink-muted mt-0.5">{t("affiliate.payout_hint", { min: minPayout })}</p>
          </div>
          <Button onClick={requestPayout} disabled={!canPayout || requesting} data-testid="affiliate-request-payout">
            {requesting ? <Loader2 size={14} className="animate-spin" /> : <Wallet size={14} />}
            {t("affiliate.request_payout")}
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-white/5">
          <div>
            <Label>{t("affiliate.payout_email")}</Label>
            <Input
              type="email"
              value={payoutEmail}
              onChange={(e) => setPayoutEmail(e.target.value)}
              placeholder="bank@example.com"
              data-testid="affiliate-payout-email"
            />
          </div>
        </div>

        {/* Payout history */}
        {data.payouts?.length > 0 && (
          <div className="pt-3 border-t border-white/5">
            <div className="text-xs uppercase tracking-wider text-ink-muted mb-2">{t("affiliate.payout_history")}</div>
            <table className="w-full text-sm" data-testid="affiliate-payouts-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-ink-muted">
                  <th className="py-2">{t("affiliate.payout_requested_at")}</th>
                  <th className="py-2">{t("affiliate.payout_amount")}</th>
                  <th className="py-2">{t("affiliate.payout_status_col")}</th>
                  <th className="py-2">{t("affiliate.payout_paid_at")}</th>
                </tr>
              </thead>
              <tbody>
                {data.payouts.map((p) => (
                  <tr key={p.id} className="border-t border-white/5">
                    <td className="py-2 text-ink-secondary">{fmtDate(p.requested_at)}</td>
                    <td className="py-2 num">{fmtEur(p.amount_eur)}</td>
                    <td className="py-2"><Badge tone={p.status === "paid" ? "ok" : p.status === "rejected" ? "err" : "warn"}>{t(`affiliate.payout_status_${p.status}`, p.status)}</Badge></td>
                    <td className="py-2 text-ink-secondary">{fmtDate(p.paid_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Recent commissions */}
      <Card className="p-5">
        <div className="text-base font-semibold mb-3 flex items-center gap-2"><FileText size={16} className="text-brand" /> {t("affiliate.recent_commissions")}</div>
        {data.recent_commissions?.length ? (
          <table className="w-full text-sm" data-testid="affiliate-commissions-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-ink-muted">
                <th className="py-2">{t("affiliate.comm_date")}</th>
                <th className="py-2">{t("affiliate.comm_purpose")}</th>
                <th className="py-2">{t("affiliate.comm_amount")}</th>
                <th className="py-2">{t("affiliate.comm_status")}</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_commissions.map((c) => (
                <tr key={c.id} className="border-t border-white/5">
                  <td className="py-2 text-ink-secondary">{fmtDate(c.created_at)}</td>
                  <td className="py-2 text-ink-secondary">{t(`affiliate.comm_purpose_${c.purpose}`, c.purpose)}</td>
                  <td className="py-2 num text-brand">{fmtEur(c.amount_eur)}</td>
                  <td className="py-2"><Badge tone={c.status === "paid" ? "ok" : c.status === "requested" ? "warn" : "default"}>{t(`affiliate.comm_status_${c.status}`, c.status)}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-ink-muted">{t("affiliate.no_commissions_yet")}</p>
        )}
      </Card>
    </div>
  );
}
